import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Sparkles, Save, Send, AlertCircle, ExternalLink } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AttachmentDropzone, { attachmentPreviewUrl, downloadAttachment } from "@/components/AttachmentDropzone";
import OriginBadge from "@/components/OriginBadge";
import { TX_TYPES, PAYMENT_METHODS, BANK_SINGLE_TYPES, BANK_TRANSFER_TYPES, COUNTERPARTY_TYPES } from "@/lib/tx";
import CounterpartyPicker, { counterpartyKindForTxType } from "@/components/CounterpartyPicker";
import BankAccountPicker from "@/components/BankAccountPicker";

const REQUIRED = ["transaction_date", "transaction_type", "description", "amount"];
const TRACKED = [
  "transaction_date", "transaction_type", "description", "amount",
  "currency", "payment_method", "counterparty_name", "reference_number",
];

const EMPTY_FORM = {
  transaction_date: "", transaction_type: "", description: "", amount: "",
  currency: "USD", payment_method: "", counterparty_name: "",
  counterparty_id: "",
  counterparty_type: "",
  bank_account_id: "",
  from_bank_account_id: "",
  to_bank_account_id: "",
  already_paid_before_system_start: false,
  reference_number: "", note: "",
};

// Phase 11 — eligible tx types for the "Already paid before system start" flag.
const HISTORICAL_ELIGIBLE = new Set([
  "expense", "supplier_invoice", "payroll",
  "reimbursement", "tax_payment", "petty_cash",
]);

export default function NewTransactionPage() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const { id: existingId } = useParams();
  // Phase 19 hot-fix #3 (2026-07-07) — when an admin (or anyone with
  // transactions.edit_posted) navigates with ?override=admin, we let the
  // form edit posted/processed/reported transactions and gather an
  // edit_reason (required min 5 chars) before the PATCH.
  const [search] = useSearchParams();
  const adminOverride = search.get("override") === "admin";
  const [editReason, setEditReason] = useState("");
  const [txId, setTxId] = useState(existingId || null);
  const [status, setStatus] = useState("draft");
  const [attachments, setAttachments] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [origins, setOrigins] = useState({});
  const [extracting, setExtracting] = useState(false);
  const [extractError, setExtractError] = useState(null);
  const [modelUsed, setModelUsed] = useState(null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(Boolean(existingId));
  const [supplierFilter, setSupplierFilter] = useState("all"); // suppliers picker chip

  // Load existing draft when /transactions/{id}/edit
  useEffect(() => {
    const load = async () => {
      if (!existingId) return;
      try {
        const { data } = await api.get(`/transactions/${existingId}`);
        setTxId(data.id);
        setStatus(data.status);
        setOrigins(data.field_origins || {});
        setForm({
          transaction_date: data.transaction_date || "",
          transaction_type: data.transaction_type || "",
          description: data.description || "",
          amount: data.amount ?? "",
          currency: data.currency || "USD",
          payment_method: data.payment_method || "",
          counterparty_name: data.counterparty_name || "",
          counterparty_id: data.counterparty_id || "",
          counterparty_type: data.counterparty_type || "",
          bank_account_id: data.bank_account_id || "",
          from_bank_account_id: data.from_bank_account_id || "",
          to_bank_account_id: data.to_bank_account_id || "",
          already_paid_before_system_start: Boolean(data.already_paid_before_system_start),
          reference_number: data.reference_number || "",
          note: data.note || "",
        });
        // Fetch attachment metas
        const metas = await Promise.all(
          (data.attachment_ids || []).map((id) =>
            api.get(`/attachments/${id}`).then((r) => r.data).catch(() => null),
          ),
        );
        setAttachments(metas.filter(Boolean));
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [existingId]);

  const setField = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
    setOrigins((o) => {
      if (!TRACKED.includes(name)) return o;
      const cur = o[name];
      if (cur === "ai") return { ...o, [name]: "ai_corrected" };
      if (!cur) return { ...o, [name]: "manual" };
      return o;
    });
  };

  const runExtraction = async (attachmentId) => {
    setExtracting(true);
    setExtractError(null);
    try {
      const { data } = await api.post(`/transactions/extract`, { attachment_id: attachmentId });
      setModelUsed(data.model_used);
      if (data.error) {
        setExtractError(data.error);
        toast.warning("AI extraction returned no data — please fill manually.");
        return;
      }
      const f = data.fields || {};
      const next = { ...form };
      const nextOrigins = { ...origins };
      const setIfPresent = (key, val) => {
        if (val !== null && val !== undefined && val !== "") {
          next[key] = val;
          nextOrigins[key] = "ai";
        }
      };
      setIfPresent("transaction_date", f.transaction_date);
      setIfPresent("transaction_type", f.transaction_type);
      setIfPresent("amount", f.amount);
      setIfPresent("currency", f.currency || "USD");
      setIfPresent("counterparty_name", f.counterparty_name);
      setIfPresent("reference_number", f.reference_number);
      setIfPresent("description", f.description);
      setIfPresent("payment_method", f.payment_method);
      setForm(next);
      setOrigins(nextOrigins);
      toast.success("AI extracted fields — review and edit as needed.");
    } catch (e) {
      setExtractError(formatApiError(e));
      toast.error(formatApiError(e));
    } finally {
      setExtracting(false);
    }
  };

  const onAttachmentsChange = (next) => {
    const isFirst = (attachments || []).length === 0 && next.length > 0;
    setAttachments(next);
    if (isFirst && !existingId) {
      runExtraction(next[0].id);
    }
  };

  const payload = useMemo(() => ({
    transaction_date: form.transaction_date || null,
    transaction_type: form.transaction_type || null,
    description: form.description || "",
    amount: form.amount === "" ? null : Number(form.amount),
    currency: form.currency || "USD",
    payment_method: form.payment_method || null,
    counterparty_name: form.counterparty_name || null,
    counterparty_id: form.counterparty_id || null,
    counterparty_type: form.counterparty_type || null,
    bank_account_id: form.bank_account_id || null,
    from_bank_account_id: form.from_bank_account_id || null,
    to_bank_account_id: form.to_bank_account_id || null,
    // Phase 35 (2026-07-17) — Source-of-Funds workflow separation
    deposit_source: form.deposit_source || null,
    deposit_source_account: form.deposit_source_account || null,
    funding_source: form.funding_source || null,
    funding_account_code: form.funding_account_code || null,
    source_of_funds: form.source_of_funds || null,
    already_paid_before_system_start: Boolean(form.already_paid_before_system_start),
    reference_number: form.reference_number || null,
    note: form.note || null,
    attachment_ids: attachments.map((a) => a.id),
    field_origins: origins,
  }), [form, attachments, origins]);

  const saveDraft = async () => {
    // Phase 19 hot-fix #3 — when editing a posted/processed/reported tx,
    // require a non-empty edit_reason (min 5 chars). Backend enforces 422
    // too; this is the friendly client-side check.
    if (adminOverride && txIsPosted && editReason.trim().length < 5) {
      toast.error("Edit reason is required (min 5 chars) when editing a posted transaction.");
      return;
    }
    setSaving(true);
    try {
      if (txId) {
        const body = { ...payload };
        if (adminOverride && txIsPosted) body.edit_reason = editReason.trim();
        await api.patch(`/transactions/${txId}`, body);
        // Phase 19 hot-fix #4 (2026-07-08) — toast text now reflects WHERE
        // the save landed. Posted edit → GL re-posted. Editable post-draft
        // status (submitted/under_review/etc.) → plain "Changes saved".
        // Draft → legacy "Draft saved" copy.
        if (adminOverride && txIsPosted) {
          toast.success("Transaction updated — GL re-posted.");
        } else if (status && status !== "draft" && status !== "missing_document") {
          toast.success("Changes saved.");
        } else {
          toast.success("Draft saved.");
        }
      } else {
        const { data } = await api.post(`/transactions`, payload);
        setTxId(data.id);
        toast.success("Draft saved.");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const validateForSubmit = () => {
    // Phase 14 (2026-06-28): attachments are no longer client-required.
    // Backend enforces only when an admin opts-in via
    // `system_config.require_attachment_for_types`.
    const missing = REQUIRED.filter((k) => !payload[k] && payload[k] !== 0);
    return missing;
  };

  const submitTx = async () => {
    // Phase 19 hot-fix #4 (2026-07-08) — only callable from a submittable
    // status (draft / missing_document / clarification_needed). The button
    // that wires this is itself hidden on other statuses, but we re-check
    // here so an edge-case (page-load race, mid-flight status change) can't
    // ever silently mutate a row under a misleading red toast.
    if (txId && !txIsSubmittable) {
      toast.error("This transaction can't be submitted from its current status.");
      return;
    }
    const missing = validateForSubmit();
    if (missing.length) {
      toast.error(`Missing: ${missing.join(", ")}`);
      return;
    }
    setSubmitting(true);
    // Snapshot the prior payload for atomic rollback if /submit fails after
    // a successful PATCH (true atomicity).
    const priorPayload = txId ? { ...payload } : null;
    let id = txId;
    try {
      if (id) {
        await api.patch(`/transactions/${id}`, payload);
      } else {
        const { data } = await api.post(`/transactions`, payload);
        id = data.id;
      }
    } catch (e) {
      toast.error(formatApiError(e));
      setSubmitting(false);
      return;
    }
    try {
      await api.post(`/transactions/${id}/submit`);
      toast.success(status === "clarification_needed"
        ? "Re-submitted to Salihi for review."
        : "Transaction submitted to Salihi.");
      navigate(`/transactions/${id}`);
    } catch (e) {
      // Atomic rollback: undo the PATCH we just applied so the user's
      // failed-submit doesn't leave a silent mutation behind.
      if (priorPayload) {
        try {
          await api.patch(`/transactions/${id}`, priorPayload);
        } catch (_) { /* best effort */ }
      }
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const POSTED = new Set(["processed", "posted", "reported"]);
  // Phase 19 hot-fix #4 (2026-07-08) — statuses where the workflow advance
  // (`/submit`) is meaningful. From any other status the form must ONLY save
  // edits and never call `/submit`, otherwise the user sees a misleading
  // "Cannot submit from current status" error AFTER the PATCH has already
  // mutated the row (silent save under a red toast).
  const SUBMITTABLE = new Set(["draft", "missing_document", "clarification_needed"]);
  // Statuses that are NEVER editable from this form (terminal / quarantine).
  const TERMINAL = new Set(["deleted_by_admin", "rejected"]);
  const txIsPosted = POSTED.has(status);
  const txIsSubmittable = SUBMITTABLE.has(status);
  const txIsTerminal = TERMINAL.has(status);
  // editable is true for:
  //   • a brand-new tx (no existingId)
  //   • draft / missing_document / clarification_needed (owner submit path)
  //   • posted, when an admin-override session is open
  //   • any other non-terminal status, when the user has the granular
  //     `transactions.edit` permission (gate enforced backend-side too)
  const editable = !existingId
    || (!txIsTerminal && (
      SUBMITTABLE.has(status)
      || (adminOverride && txIsPosted)
      || (can && can("transactions.edit") && !txIsPosted)
    ));

  // Phase 7 — counterparty kind derived from current transaction type.
  const cpKind = counterpartyKindForTxType(form.transaction_type);

  const handleTypeChange = (newType) => {
    const prevKind = counterpartyKindForTxType(form.transaction_type);
    const nextKind = counterpartyKindForTxType(newType);
    setField("transaction_type", newType);
    // Auto-derive a sensible counterparty_type for the new tx type.
    const autoCpType = nextKind === "suppliers" ? "supplier"
      : nextKind === "customers" ? "customer"
      : nextKind === "employees" ? "employee" : "";
    // Reset counterparty when switching to a different kind (e.g. supplier→employee).
    if (prevKind !== nextKind) {
      setForm((f) => ({
        ...f,
        counterparty_id: "",
        counterparty_name: "",
        counterparty_type: autoCpType || f.counterparty_type,
      }));
      setSupplierFilter("all");
    } else if (autoCpType) {
      setForm((f) => ({ ...f, counterparty_type: autoCpType }));
    }
    // Clear bank-specific fields when not relevant
    if (!BANK_SINGLE_TYPES.includes(newType) && !BANK_TRANSFER_TYPES.includes(newType)) {
      setForm((f) => ({ ...f, bank_account_id: "", from_bank_account_id: "", to_bank_account_id: "" }));
    }
    // Phase 11 — clear historical-paid flag when switching to an ineligible type.
    if (!HISTORICAL_ELIGIBLE.has(newType)) {
      setForm((f) => ({ ...f, already_paid_before_system_start: false }));
    }
  };

  const onPickCounterparty = (id, entity) => {
    setForm((f) => {
      const next = { ...f, counterparty_id: id || "", counterparty_name: entity?.name || "" };
      // Payroll convenience: auto-fill amount with employee base_salary when picked
      if (f.transaction_type === "payroll" && entity?.base_salary != null
          && (f.amount === "" || f.amount === 0 || f.amount === "0")) {
        next.amount = String(entity.base_salary);
        setOrigins((o) => ({ ...o, amount: "manual" }));
      }
      return next;
    });
    setOrigins((o) => ({
      ...o,
      counterparty_name: o.counterparty_name === "ai" ? "ai_corrected" : "manual",
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-[#4FA3D1]" />
      </div>
    );
  }

  return (
    <div className="grid gap-6 lg:grid-cols-3" data-testid="new-transaction-page">
      <div className="lg:col-span-2 space-y-6">
        {/* Step 1 — Upload */}
        <Card>
          <CardHeader>
            <CardTitle>1. Upload supporting document (optional)</CardTitle>
            <CardDescription>
              Attach the invoice, receipt, voucher or bank slip. AI will extract the fields automatically.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <AttachmentDropzone
              attachments={attachments}
              onChange={onAttachmentsChange}
              testid="tx-dropzone"
            />
            {attachments.length > 0 && (
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <Button
                  type="button"
                  onClick={() => runExtraction(attachments[0].id)}
                  disabled={extracting}
                  className="bg-[#4FA3D1] hover:bg-[#2F86B5] text-white"
                  data-testid="extract-button"
                >
                  {extracting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Extracting…
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-4 w-4" /> Extract with AI
                    </>
                  )}
                </Button>
                {modelUsed && (
                  <span className="text-xs text-slate-500" data-testid="model-used">
                    Model: <code>{modelUsed}</code>
                  </span>
                )}
              </div>
            )}
            {extractError && (
              <Alert variant="destructive" className="mt-3" data-testid="extract-error">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{extractError}</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Step 2 — Fields */}
        <Card>
          <CardHeader>
            <CardTitle>2. Transaction details</CardTitle>
            <CardDescription>
              Review the extracted values. Edit any field — its badge will flip to <em>Corrected</em>.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <FieldWrap label="Date" required origin={origins.transaction_date}>
                <Input
                  type="date"
                  value={form.transaction_date}
                  disabled={!editable}
                  onChange={(e) => setField("transaction_date", e.target.value)}
                  data-testid="field-transaction_date"
                />
              </FieldWrap>

              <FieldWrap label="Type" required origin={origins.transaction_type}>
                <Select
                  value={form.transaction_type || ""}
                  onValueChange={(v) => handleTypeChange(v)}
                  disabled={!editable}
                >
                  <SelectTrigger data-testid="field-transaction_type">
                    <SelectValue placeholder="Select…" />
                  </SelectTrigger>
                  <SelectContent>
                    {TX_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FieldWrap>

              <FieldWrap label="Amount (USD)" required origin={origins.amount}>
                <Input
                  type="number"
                  step="0.01"
                  value={form.amount}
                  disabled={!editable}
                  onChange={(e) => setField("amount", e.target.value)}
                  data-testid="field-amount"
                />
              </FieldWrap>

              <FieldWrap label="Payment method" origin={origins.payment_method}>
                <Select
                  value={form.payment_method || ""}
                  onValueChange={(v) => setField("payment_method", v)}
                  disabled={!editable}
                >
                  <SelectTrigger data-testid="field-payment_method">
                    <SelectValue placeholder="Select…" />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((m) => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FieldWrap>

              <FieldWrap label="Counterparty" origin={origins.counterparty_name}>
                {cpKind ? (
                  <div className="space-y-1.5">
                    <CounterpartyPicker
                      kind={cpKind}
                      value={form.counterparty_id}
                      onChange={onPickCounterparty}
                      disabled={!editable}
                      placeholder={`Select ${cpKind.slice(0, -1)}…`}
                      filterConsultant={cpKind === "suppliers" ? supplierFilter : undefined}
                      onFilterConsultantChange={cpKind === "suppliers" ? setSupplierFilter : undefined}
                    />
                    <Link
                      to={`/${cpKind}`}
                      className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-[#2F86B5]"
                      data-testid={`counterparty-list-link-${cpKind}`}
                    >
                      <ExternalLink className="h-3 w-3" />
                      Open {cpKind} list
                    </Link>
                  </div>
                ) : (
                  <Input
                    value={form.counterparty_name}
                    disabled={!editable}
                    onChange={(e) => setField("counterparty_name", e.target.value)}
                    data-testid="field-counterparty_name"
                    placeholder="Supplier, customer, employee…"
                  />
                )}
              </FieldWrap>

              {/* Phase 10 — structured counterparty type (always visible) */}
              <FieldWrap label="Counterparty type">
                <Select
                  value={form.counterparty_type || ""}
                  onValueChange={(v) => setField("counterparty_type", v)}
                  disabled={!editable}
                >
                  <SelectTrigger data-testid="field-counterparty_type">
                    <SelectValue placeholder="Select…" />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTERPARTY_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FieldWrap>

              {/* Phase 10 — Bank Account dropdowns shown only when relevant */}
              {BANK_SINGLE_TYPES.includes(form.transaction_type) && (
                <div className="sm:col-span-2">
                  <FieldWrap label="Bank account" required>
                    <BankAccountPicker
                      value={form.bank_account_id}
                      onChange={(id) => setField("bank_account_id", id)}
                      disabled={!editable}
                      testid="field-bank_account"
                    />
                  </FieldWrap>
                </div>
              )}
              {/* Phase 35 (2026-07-17) — Source-of-Funds workflow separation
                  so users classify shareholder-loan inflows correctly and
                  never accidentally hit Cash on Hand (1000) or Opening
                  Balance Equity (3100). */}
              {(form.transaction_type === "bank_deposit"
                || form.transaction_type === "beginning_bank_balance") && (
                <div className="sm:col-span-2">
                  <FieldWrap label="Source of Funds" required>
                    <Select
                      value={form.source_of_funds || ""}
                      onValueChange={(v) => {
                        setField("source_of_funds", v);
                        // Mirror to the backend fields
                        if (form.transaction_type === "beginning_bank_balance") {
                          setField("funding_source",
                            v === "loan_shareholder" || v === "opening_from_loan" ? "loan" : "equity");
                          if (v === "loan_shareholder" || v === "opening_from_loan") {
                            setField("funding_account_code", "2200");
                          }
                        } else {
                          setField("deposit_source",
                            v === "loan_shareholder" ? "loan_drawdown"
                            : v === "physical_cash" ? "cash_on_hand"
                            : v === "customer_payment" ? "customer_payment"
                            : "cash_on_hand");
                          if (v === "loan_shareholder") {
                            setField("deposit_source_account", "2200");
                          }
                        }
                      }}
                      disabled={!editable}
                    >
                      <SelectTrigger data-testid="field-source_of_funds">
                        <SelectValue placeholder="Select the source of this inflow" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="loan_shareholder" data-testid="sof-loan-shareholder">
                          Loan receipt from shareholder/lender — credits Loan Payable (2200)
                        </SelectItem>
                        {form.transaction_type === "beginning_bank_balance" && (
                          <SelectItem value="opening_from_loan" data-testid="sof-opening-loan">
                            Opening balance funded by shareholder loan — credits Loan Payable (2200)
                          </SelectItem>
                        )}
                        {form.transaction_type === "bank_deposit" && (
                          <>
                            <SelectItem value="customer_payment" data-testid="sof-customer-payment">
                              Customer payment received — clears Accounts Receivable
                            </SelectItem>
                            <SelectItem value="physical_cash" data-testid="sof-physical-cash">
                              Physical cash deposit into bank — Cr Cash on Hand
                            </SelectItem>
                          </>
                        )}
                        <SelectItem value="bank_transfer_hint" disabled>
                          — For bank-to-bank transfers, use the "Bank Transfer" transaction type —
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </FieldWrap>
                  {form.source_of_funds === "physical_cash" && (
                    <div
                      className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800"
                      data-testid="physical-cash-warning-banner"
                    >
                      ⚠️ Cash on Hand balance is <strong>$0.00</strong>. Did you mean
                      &nbsp;<em>Loan receipt from shareholder</em> or a <em>Bank-to-bank transfer</em>?
                      Physical cash deposits should only be posted when actual notes/coins
                      are moved from the Cash on Hand tin into a bank.
                    </div>
                  )}
                </div>
              )}
              {BANK_TRANSFER_TYPES.includes(form.transaction_type) && (
                <>
                  <FieldWrap label="From bank account" required>
                    <BankAccountPicker
                      value={form.from_bank_account_id}
                      onChange={(id) => setField("from_bank_account_id", id)}
                      disabled={!editable}
                      excludeId={form.to_bank_account_id || undefined}
                      testid="field-from_bank_account"
                    />
                  </FieldWrap>
                  <FieldWrap label="To bank account" required>
                    <BankAccountPicker
                      value={form.to_bank_account_id}
                      onChange={(id) => setField("to_bank_account_id", id)}
                      disabled={!editable}
                      excludeId={form.from_bank_account_id || undefined}
                      testid="field-to_bank_account"
                    />
                  </FieldWrap>
                </>
              )}

              <FieldWrap label="Reference #" origin={origins.reference_number}>
                <Input
                  value={form.reference_number}
                  disabled={!editable}
                  onChange={(e) => setField("reference_number", e.target.value)}
                  data-testid="field-reference_number"
                  placeholder="Invoice / receipt number"
                />
              </FieldWrap>

              <div className="sm:col-span-2">
                <FieldWrap label="Description" required origin={origins.description}>
                  <Input
                    value={form.description}
                    disabled={!editable}
                    onChange={(e) => setField("description", e.target.value)}
                    data-testid="field-description"
                    placeholder="What is this transaction for?"
                  />
                </FieldWrap>
              </div>

              {/* Phase 11 — Already Paid Before System Start toggle */}
              {HISTORICAL_ELIGIBLE.has(form.transaction_type) && (
                <div className="sm:col-span-2">
                  <label
                    className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50/60 p-3 cursor-pointer hover:bg-amber-50"
                    data-testid="field-historical-toggle"
                  >
                    <input
                      type="checkbox"
                      checked={Boolean(form.already_paid_before_system_start)}
                      disabled={!editable}
                      onChange={(e) => setField("already_paid_before_system_start", e.target.checked)}
                      className="mt-1 h-4 w-4"
                      data-testid="field-already_paid_before_system_start"
                    />
                    <div>
                      <div className="text-sm font-medium text-amber-900">
                        Already paid before system start
                      </div>
                      <div className="text-xs text-amber-800/80 mt-0.5">
                        Historical record only — bank / cash balance will NOT be deducted.
                        JE posts <code className="bg-amber-100 px-1 rounded">Dr&nbsp;Expense&nbsp;/&nbsp;Cr&nbsp;3200&nbsp;Historical&nbsp;Paid&nbsp;Clearing</code>.
                      </div>
                    </div>
                  </label>
                </div>
              )}

              <div className="sm:col-span-2">
                <Label htmlFor="note" className="text-sm font-medium">Note (optional)</Label>
                <Textarea
                  id="note"
                  value={form.note}
                  disabled={!editable}
                  onChange={(e) => setField("note", e.target.value)}
                  data-testid="field-note"
                  rows={3}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Phase 19 hot-fix #3 (2026-07-07) — when editing a posted tx
                via admin override, an audit-trail reason is required before
                save. Backend enforces 422 if missing. */}
            {adminOverride && txIsPosted && (
              <Alert className="mt-6 border-amber-300 bg-amber-50" data-testid="edit-reason-alert">
                <AlertCircle className="h-4 w-4 text-amber-700" />
                <AlertDescription className="text-amber-900">
                  <p className="font-medium">Admin override on a posted transaction.</p>
                  <p className="text-sm mt-0.5">
                    Saving this edit will <strong>reverse the existing journal entry</strong> and
                    <strong> post a fresh one</strong>. A reason is required (min 5 chars) for the audit log.
                  </p>
                  <div className="mt-3">
                    <Label htmlFor="edit-reason" className="text-amber-900">Reason for this edit</Label>
                    <Textarea
                      id="edit-reason"
                      value={editReason}
                      onChange={(e) => setEditReason(e.target.value)}
                      placeholder="Why is this posted transaction being edited?"
                      rows={2}
                      className="mt-1 bg-white"
                      data-testid="edit-reason-input"
                    />
                  </div>
                </AlertDescription>
              </Alert>
            )}

            <div className="mt-6 flex flex-wrap gap-3 justify-end">
              <Button asChild variant="outline" type="button">
                <Link to="/transactions">Cancel</Link>
              </Button>
              {/* Phase 19 hot-fix #4 (2026-07-08) — primary CTA is now driven
                  by the current tx status. We never render BOTH a "Save"
                  AND a "Submit" button at once unless the tx is in a
                  submittable status (draft / missing_document /
                  clarification_needed); otherwise the only primary action
                  is "Save changes" (PATCH-only, no workflow advance) so a
                  failed /submit can never silently mutate the row.

                  Posted edit via admin override is also Save-only (the
                  GL re-post happens server-side as a side-effect of PATCH).
              */}
              {txIsSubmittable && !(adminOverride && txIsPosted) ? (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={saveDraft}
                    disabled={saving || !editable}
                    data-testid="save-draft-button"
                  >
                    {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                    Save draft
                  </Button>
                  <Button
                    type="button"
                    onClick={submitTx}
                    disabled={submitting || !editable}
                    className="bg-[#4FA3D1] hover:bg-[#2F86B5] text-white"
                    data-testid="submit-tx-button"
                  >
                    {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                    {status === "clarification_needed" ? "Re-submit" : "Submit to Salihi"}
                  </Button>
                </>
              ) : (
                <Button
                  type="button"
                  onClick={saveDraft}
                  disabled={saving || !editable}
                  className="bg-[#4FA3D1] hover:bg-[#2F86B5] text-white"
                  data-testid="save-changes-button"
                >
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                  Save changes
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Right column — preview */}
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>Document preview</CardTitle>
            <CardDescription>{attachments.length === 0 ? "No file uploaded yet." : attachments[0].original_name}</CardDescription>
          </CardHeader>
          <CardContent>
            {attachments.length > 0 ? (
              <div className="space-y-3">
                <div className="rounded-md border border-slate-200 bg-slate-50 p-6 text-center">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">{attachments[0].mime_type}</p>
                  <p className="text-sm font-medium">{attachments[0].original_name}</p>
                </div>
                <Button
                  onClick={() => downloadAttachment(attachments[0].id, attachments[0].original_name)}
                  variant="outline"
                  className="w-full"
                  data-testid="preview-link"
                >Download to preview</Button>
              </div>
            ) : (
              <p className="text-sm text-slate-400 italic">Upload a file to preview it here.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function FieldWrap({ label, required, origin, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <Label className="text-sm font-medium text-slate-700">
          {label}{required && <span className="text-red-500 ml-0.5">*</span>}
        </Label>
        {origin ? <OriginBadge origin={origin} /> : null}
      </div>
      {children}
    </div>
  );
}
